import { defineStore } from 'pinia'
import { structureTreeTableService } from '@/modules/tenant-admin/services/structure/structure-treetable.service.js'
import {
  getDisplayValue,
  prepareValueForSave,
} from '@/shared/utils/localization-helper'

export const useProcessStructureStore = defineStore(
  'tenant-admin-process-structure',
  {
    state: () => ({
      /**
       * Tree table nodes data
       */
      nodeTree: [],
      /**
       * Current Working
       */
      workingNode: null,

      /**
       * Node Info
       */
      nodeInfo: null,

      /**
       * Data to be searched
       */
      filterText: '',
      /**
       * Track if the `type` field has a validation error
       */
      validationErrors: {
        type: false,
      },
    }),

    getters: {
      /**
       * Form validate
       * @param {operationAreaTypes} operationAreaTypes
       * @returns {boolean} If all data is filled
       */
      isValid: ({ workingNode, validationErrors }) => {
        if (!workingNode)
          return false

        const { name, responsible } = workingNode.data || {}

        return (
          !!name
          && !validationErrors.name // Ensure name exists and has no validation errors
          && !!responsible // Responsible must be present
        )
      },

      isActivityValid: ({ workingNode }) => {
        if (!workingNode)
          return false

        const { responsible, nameLang } = workingNode.data || {}

        return (
          !!nameLang // Ensure name exists and has no validation errors
          && Object.values(nameLang).some(v => typeof v === 'string' && v.trim().length > 0)
          && !!responsible // Responsible must be present
        )
      },

      isMacroProcessValid: ({ workingNode }) => {
        if (!workingNode)
          return false

        const { responsible, area, nameLang } = workingNode.data || {}

        return (
          !!nameLang // Ensure name exists and has no validation errors
          && Object.values(nameLang).some(v => typeof v === 'string' && v.trim().length > 0)
          && !!responsible // Responsible must be present
          && !!area // Area must be present
        )
      },

      isProcessValid: ({ workingNode }) => {
        if (!workingNode)
          return false

        const { responsible, area, nameLang } = workingNode.data || {}

        return (
          !!nameLang // Ensure name exists and has no validation errors
          && Object.values(nameLang).some(v => typeof v === 'string' && v.trim().length > 0)
          && !!responsible // Responsible must be present
          && !!(area && area.length) // Area must have a valid length
        )
      },

      /**
       * Returns the current work node
       *
       * @returns {Array} Array of objects
       */
      getWorkingNodeData: ({ workingNode }) => {
        return workingNode?.data || {}
      },

      /**
       * Returns the prepared working node for API operations
       * Ensures names are properly formatted
       *
       * @returns {object} Prepared node
       */
      getWorkingNode: (state) => {
        if (!state.workingNode)
          return null

        const preparedNode = { ...state.workingNode }

        preparedNode.data = { ...state.workingNode.data }

        if (preparedNode.data.name) {
          preparedNode.data.name = prepareValueForSave(preparedNode.data.name)
        }

        return preparedNode
      },

      getNodeInfo: ({ nodeInfo }) => {
        return nodeInfo.node
      },

      /**
       * Returns the display name of the working node
       */
      workingNodeDisplayName: (state) => {
        return state.workingNode
          ? getDisplayValue(state.workingNode.data.name)
          : ''
      },

      /**
       * Returns the filtered tree data based on filterText.
       */
      filteredTreeData(state) {
        if (!state.filterText) {
          return state.nodeTree
        }

        const searchText = state.filterText.toLowerCase()

        const nodeMatchesFilter = (node) => {
          const nameValue = getDisplayValue(node.data.name)
          const value = String(nameValue || '').toLowerCase()

          if (value.includes(searchText)) {
            return true
          }

          if (node.children && node.children.length > 0) {
            return node.children.some(child => nodeMatchesFilter(child))
          }

          return false
        }

        return state.nodeTree.filter(node => nodeMatchesFilter(node))
      },

      /**
       * Get the hierarchy for a macroProcess node.
       * @returns {string} - The hierarchy for the node.
       */
      getBusinessUnitName: (state) => {
        if (
          !state.nodeInfo
          || !state.nodeInfo.hierarchy
          || state.nodeInfo.hierarchy.length === 0
        ) {
          return ''
        }
        return getDisplayValue(state.nodeInfo.hierarchy[0])
      },

      /**
       * Get the hierarchy for a process node.
       * @returns {string} - The hierarchy for the node.
       */
      getMacroProcessName: (state) => {
        if (
          !state.nodeInfo
          || !state.nodeInfo.hierarchy
          || state.nodeInfo.hierarchy.length < 2
        ) {
          return ''
        }
        return getDisplayValue(state.nodeInfo.hierarchy[1])
      },

      /**
       * Get the hierarchy for an activity node.
       * @returns {string} - The hierarchy for the node.
       */
      getProcessName: (state) => {
        if (
          !state.nodeInfo
          || !state.nodeInfo.hierarchy
          || state.nodeInfo.hierarchy.length < 3
        ) {
          return ''
        }
        return getDisplayValue(state.nodeInfo.hierarchy[2])
      },
    },

    actions: {
      /**
       * Create a new empty business are working  node
       */
      newWorkingNode() {
        this.workingNode = structureTreeTableService.newNodeItem({
          name: { en: '' },
          nameLang: null,
          area: null,
          responsible: null,
          active: true,
        })
      },

      /**
       * Clear Business Unit working Node
       */
      clearWorkingNode() {
        this.workingNode = null
      },
      /**
       * Set data content into property of the working Node
       *
       * @param propertyName
       * @param value
       */
      setWorkingNodeDataProperty(propertyName, value) {
        this.workingNode.data[propertyName] = value
      },
      /**
       * Set working node for edit
       * @param nodeKey
       */
      setWorkingNodeData(nodeKey) {
        this.workingNode = structureTreeTableService.findParentNodeById(
          this.nodeTree,
          nodeKey,
        )
      },
      setWorkingNode(node) {
        // Create a copy to avoid modifying the original
        const processedNode = { ...node }

        if (node.data) {
          // Copy data to avoid mutating
          processedNode.data = { ...node.data }

          // Handle multiple business areas
          if (
            'businessAreas' in node.data
            && Array.isArray(node.data.businessAreas)
          ) {
            processedNode.data.area = node.data.businessAreas.map(area => ({
              label: area.name,
              value: area._id,
            }))
          }
          // Handle single business area
          else if ('businessArea' in node.data) {
            const businessArea = node.data.businessArea
            if (businessArea) {
              processedNode.data.area = {
                label: businessArea.name,
                value: businessArea._id,
              }
            }
          }
        }

        this.workingNode = processedNode
      },
      /**
       * Set node info
       * @param nodeInfo
       */
      setNodeInfoData(nodeInfo) {
        // Create a processed version of nodeInfo with properly formatted hierarchy
        const processedNodeInfo = {
          ...nodeInfo,
          // Ensure hierarchy items are properly displayed
          hierarchy: nodeInfo.hierarchy.map(item =>
            // If item is just a string, keep it as is
            typeof item === 'string' ? item : getDisplayValue(item),
          ),
        }

        this.nodeInfo = processedNodeInfo
      },
      /**
       * Set all companies that belongs to tenant admin
       *
       * @param nodeTree
       * @param companyUsers
       * @param tooltipConfig
       * @param t i!8n instance
       */
      setNodeTreeData(nodeTree, companyUsers, tooltipConfig, t) {
        // First update with responsibles as before
        let processedTree
          = structureTreeTableService.updateNodeTreeWithResponsible(
            nodeTree,
            companyUsers,
          )

        // Then add tooltips as before
        processedTree = structureTreeTableService.addTooltipsToNodes(
          processedTree,
          tooltipConfig,
          t,
        )

        this.nodeTree = this.processNodeDisplayNames(processedTree)
      },
      /**
       * Process node names for display throughout the entire tree
       * This adds a displayName field to each node's data object
       */
      processNodeDisplayNames(nodes) {
        return nodes.map((node) => {
          const processedNode = { ...node }

          processedNode.data = {
            ...node.data,
            displayName: getDisplayValue(node.data.name),
          }

          if (processedNode.children && processedNode.children.length > 0) {
            processedNode.children = this.processNodeDisplayNames(
              processedNode.children,
            )
          }

          return processedNode
        })
      },
      /**
       * Prepare the working node data for API submission
       * Creates a copy with properly formatted fields
       */
      prepareWorkingNodeForApi() {
        if (!this.workingNode)
          return null

        const preparedData = { ...this.workingNode.data }

        if (preparedData.name !== undefined) {
          preparedData.name = prepareValueForSave(preparedData.name)
        }

        return preparedData
      },
      setValidationError(field, hasError) {
        this.validationErrors[field] = hasError
      },
    },
  },
)
